import React, { useState, useEffect } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';

import createLink from 'utils/createLink';

import { MetaContainer } from 'containers/meta/MetaContainer';
import { SocialContainer } from 'containers/social/Social';
import { Slices } from 'containers/slices/Slices';

import ArticleGridMasonry from 'components/article-grid/ArticleGridMasonry';
import RichText from 'components/rich-text/RichText';
import { ArticleCard } from 'components/article-card/ArticleCard';
import { CategoryHero } from 'components/category-hero/CategoryHero';
import { Content } from 'components/content/Content';
import { HeroBackground } from 'components/hero-background/HeroBackground';
import { Intro } from 'components/intro/Intro';
import { PrismicImage } from 'components/prismic-image/PrismicImage';
import { Segment } from 'components/layout/Segment';
import { Tabs } from 'components/tabs/Tabs';
import { TabsItem } from 'components/tabs/TabsItem';

interface IAboutIcelandPageContext {
  page: number;
  total: number;
  lang: string;
  uid: string;
  filter?: string;
}

interface IProps {
  data: any;
  location: Location;
  prismic: any;
  pageContext: IAboutIcelandPageContext;
  filter: string;
  intl: IntlShape;
}

const CategoryContainerComponent = (props: IProps) => {
  const { data: initialData, location, intl, pageContext: context } = props;

  if (!initialData) {
    return null;
  }

  const data = initialData.prismic;

  // Need to be done somehow else
  const pageData = get(data, 'allAbout_icelands.edges.0.node', undefined) ||
    get(data, 'allThings_to_dos.edges.0.node', undefined) ||
    get(data, 'allPlan_your_trips.edges.0.node', undefined) ||
    {}
  ;

  const sliceData = get(pageData, 'body', []) || [];
  const items = get(pageData, 'items', []) || [];

  const currentFilter = context.filter;

  const [activeFilter, setActiveFilter] = useState(currentFilter || '');
  const [activeItems, setActiveItems] = useState(items);

  useEffect(() => {
    const filtered = items.filter(({ item }: any): boolean => {
      if (!activeFilter) {
        return true;
      }

      if (item && item.category && item.category._meta) {
        return item.category._meta.uid === activeFilter;
      }

      return false;
    });

    setActiveItems(filtered);
  }, [activeFilter]);

  useEffect(() => {
    const onPopState = () => {
      const split = window.location.pathname.split('/');
      const filter = split.length > 2 ? split[2] : '';
      setActiveFilter(filter);
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const onTabClick = (cat: string) => {
    window.history.pushState({ }, '', `/${context.uid}/${cat}`);
    setActiveFilter(cat);
  };

  const hardcodedFilters = get<any[]>(pageData, 'filters', [])
    // filter away null filters which might be caused by missing translations
    .filter((i) => i === null || (i.item && i.item !== null));

  const filtersLinks = new Map();

  if (hardcodedFilters.length > 0) {
    hardcodedFilters.forEach(({ item }: any) => {
      filtersLinks.set(RichText.asText(item.title), { category: item });
    });
  } else {
    items.forEach(({ item }: any) => {
      if (item && item.category) {
        filtersLinks.set(RichText.asText(item.category.title), item);
      }
    });
  }

  const filterSource = hardcodedFilters.length > 0 ? hardcodedFilters : items;
  filterSource.forEach(({ item }: any) => {
    if (item && item.category) {
      filtersLinks.set(RichText.asText(item.category.title), item);
    }
  });

  const defaultTab = (
    <TabsItem
      id=""
      key="default-filter"
      active={activeFilter === ''}
    >
      {intl.formatMessage({ id: 'all-articles', defaultMessage: 'All Articles' })}
    </TabsItem>
  );

  const renderFilter = ([title, item]: any = []) => {
    if (!item || !title) {
      return null;
    }

    const { category: { _meta: { uid = '' } = {}} = {} } = item;

    return (
      <TabsItem
        id={uid}
        key={uid}
        active={uid === activeFilter}
      >
        {title}
      </TabsItem>
    );
  };

  const renderItem = ({ item }: any) => {
    if (isEmpty(item) || isEmpty(item.category)) {
      return;
    }

    const image = !isEmpty(item.image) && !isEmpty(item.image.x1) ?
      <PrismicImage image={item.image} view="x1" /> : null ;

    return (
      <ArticleCard
        key={item._meta.uid}
        link={createLink(item)}
        heading={RichText.asText(item.title)}
        description={RichText.asText(item.list_description)}
        category={RichText.asText(item.category.title)}
        image={image}
        align={get(item, 'text_alignment', 'left').toLowerCase()}
      />
    );
  };

  return (
    <>
      <MetaContainer data={pageData} />

      <CategoryHero
        heading={RichText.asText(pageData.subtitle)}
        kicker={RichText.asText(pageData.title)}
        align={get(pageData, 'hero_align', 'right').toLowerCase()}
      >
        <HeroBackground
          video={get(pageData, 'video.url', '')}
          image={!isEmpty(pageData.image) && get(pageData, 'image.x1.url', '') || ''}
          description={RichText.asText(pageData.image_caption) || get(pageData.image, 'alt', '') || ''}
          credits={RichText.asText(pageData.image_author) || get(pageData.image, 'copyright', null)}
        />
      </CategoryHero>

      <Content>
        <SocialContainer type="sidebar" facebookLink="/" twitterLink="/" pinterestLink="/" />

        <Segment>
          <Intro>
            {RichText.asText(get(pageData, 'description', ''))}
          </Intro>
        </Segment>

        <Segment>
          <Tabs location={location} onClick={onTabClick}>
            {defaultTab}
            {Array.from(filtersLinks).map(renderFilter)}
          </Tabs>

          <ArticleGridMasonry>
            {activeItems && activeItems.length > 0 && activeItems.map(renderItem)}
          </ArticleGridMasonry>
        </Segment>

          {!isEmpty(sliceData) &&
            <Slices data={sliceData} />
          }
        </Content>
    </>
  );
};

export const CategoryContainer = injectIntl(CategoryContainerComponent);
